import { Activity, UserPlus, UserCheck } from 'lucide-react'
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { CHART_COLORS } from '../../utils/dashboardConstants'

export default function AdminCharts({ userStats, weeklyStats }) {
  const pieData = [
    { name: 'Total Users', value: userStats.totalUsers, color: CHART_COLORS.totalUsers },
    { name: 'Active (7d)', value: userStats.activeUsers, color: CHART_COLORS.activeUsers },
    { name: 'Online Now', value: userStats.totalLoggedIn, color: CHART_COLORS.onlineNow },
    { name: 'New Today', value: userStats.totalUsersCreated, color: CHART_COLORS.newToday },
    { name: 'Deleted', value: userStats.deletedUsers, color: CHART_COLORS.deleted }
  ]

  const tooltipStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    border: '1px solid rgba(0,0,0,0.1)',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      {/* Donut Chart - User Distribution */}
      <div className="relative backdrop-blur-xl bg-white/70 rounded-2xl shadow-lg border border-white/40 p-6 overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-blue-400/10 rounded-full blur-2xl"></div>

        <div className="relative">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
                Distribusi User
              </h3>
              <p className="text-xs text-gray-500 mt-1">Breakdown statistik user</p>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-blue-100 p-2 rounded-lg">
              <Activity className="w-5 h-5 text-blue-600" />
            </div>
          </div>

          <div className="chart-container flex items-center justify-center">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-xs text-gray-600">Total Users</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className="text-xs text-gray-600">Active (7d)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-xs text-gray-600 flex items-center gap-1">
                Online Now
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-xs text-gray-600">New Today</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-xs text-gray-600">Deleted</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bar Chart - Weekly Users */}
      <div className="relative backdrop-blur-xl bg-white/70 rounded-2xl shadow-lg border border-white/40 p-6 overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400/10 to-emerald-400/10 rounded-full blur-2xl"></div>

        <div className="relative">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                User Baru (7 Hari)
              </h3>
              <p className="text-xs text-gray-500 mt-1">Tren pendaftaran mingguan</p>
            </div>
            <div className="bg-gradient-to-br from-green-100 to-emerald-100 p-2 rounded-lg">
              <UserPlus className="w-5 h-5 text-green-600" />
            </div>
          </div>

          <div className="chart-container">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={weeklyStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value) => [value + ' user', 'Pendaftar']}
                  labelFormatter={(label) => {
                    const item = weeklyStats.find(s => s.day === label)
                    return item ? `${label} (${item.date})` : label
                  }}
                />
                <Bar
                  dataKey="users"
                  fill="url(#barGradient)"
                  radius={[8, 8, 0, 0]}
                  maxBarSize={50}
                />
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0.6} />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Summary */}
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-green-600" />
              <span className="text-sm text-gray-600">Total minggu ini:</span>
            </div>
            <span className="text-lg font-bold text-green-600">
              {weeklyStats.reduce((sum, day) => sum + day.users, 0)} user
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
